import React, { useEffect, useMemo, useState } from "react";
import api, { downloadFile, authFileUrl, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Printer, QrCode, FileText, Tag, PackageOpen, Download, Plus, Trash2, Eye, ReceiptText, Building2 } from "lucide-react";

const money = (n) => `${(Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

async function downloadPost(path, body, filename) {
  const res = await api.post(path, body, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

const TABS = [
  { v: "qr", l: "QR by PO", icon: QrCode },
  { v: "assembly", l: "Assembly Instructions", icon: FileText },
  { v: "labels", l: "Export Labels", icon: Tag },
  { v: "packing", l: "Packing List", icon: PackageOpen },
  { v: "invoice", l: "Commercial Invoice", icon: ReceiptText },
];

export default function Printing() {
  const [tab, setTab] = useState("qr");
  const [pos, setPos] = useState([]);
  const [costings, setCostings] = useState([]);

  useEffect(() => {
    api.get("/purchase-orders").then((r) => setPos(r.data)).catch(() => {});
    api.get("/costings").then((r) => setCostings(r.data)).catch(() => {});
  }, []);

  const skus = useMemo(() => costings.filter((c) => c.sku).map((c) => ({
    sku: c.sku, product_name: c.product_name, net_weight: c.net_weight, gross_weight: c.gross_weight,
    assembly_pdf: c.assembly_pdf, boxes: c.boxes, box_l: c.box_l, box_b: c.box_b, box_h: c.box_h,
    box_unit: c.box_unit, num_boxes: c.num_boxes,
    final_price_usd: c.final_price_usd, final_price_inr: c.final_price_inr,
  })), [costings]);

  const dimsOf = (c) => {
    if (!c) return "";
    const u = c.box_unit || "cm";
    if (c.boxes?.length) return c.boxes.filter((b) => b.l || b.b || b.h).map((b) => `${b.l}x${b.b}x${b.h}`).join("; ") + ` ${u}`;
    if (c.box_l || c.box_b || c.box_h) return `${c.box_l || 0}x${c.box_b || 0}x${c.box_h || 0} ${u}`;
    return "";
  };

  return (
    <div className="space-y-8">
      <PageHeader title="Printing" subtitle="QR labels, assembly instructions, export carton labels & packing lists." icon={Printer} />

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.v} data-testid={`print-tab-${t.v}`} onClick={() => setTab(t.v)}
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors duration-200 ${tab === t.v ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:border-primary/40"}`}>
            <t.icon className="h-4 w-4" /> {t.l}
          </button>
        ))}
      </div>

      {tab === "qr" && <QrByPo pos={pos} />}
      {tab === "assembly" && <Assembly skus={skus} />}
      {tab === "labels" && <ExportLabels skus={skus} dimsOf={dimsOf} />}
      {tab === "packing" && <PackingList skus={skus} pos={pos} />}
      {tab === "invoice" && <Invoice skus={skus} pos={pos} />}
    </div>
  );
}

function SectionCard({ title, hint, children }) {
  return (
    <Card className="rounded-md border-border p-5 shadow-none sm:p-6">
      <h3 className="font-heading text-base font-bold">{title}</h3>
      {hint && <p className="mb-4 mt-1 text-sm text-muted-foreground">{hint}</p>}
      <div className={hint ? "" : "mt-4"}>{children}</div>
    </Card>
  );
}

/* 1. QR labels per carton for a PO */
function QrByPo({ pos }) {
  const [pid, setPid] = useState("");
  const po = pos.find((p) => p.id === pid);
  const dl = async () => {
    try { await downloadFile(`/printing/po/${pid}/qr-labels.pdf`, `${po?.po_number || "PO"}_qr_labels.pdf`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No labels to print"); }
  };
  return (
    <SectionCard title="QR labels by Purchase Order" hint="Pick a PO — one QR label is generated per carton (carton count comes from the SKU's cost sheet). Each QR encodes PO · SKU · box number.">
      <div className="grid max-w-xl gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
        <div className="space-y-1">
          <Label className="text-xs">Purchase Order</Label>
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger data-testid="qr-po-select"><SelectValue placeholder="Select a PO" /></SelectTrigger>
            <SelectContent>
              {pos.length === 0 ? <div className="px-2 py-1.5 text-xs text-muted-foreground">No purchase orders</div>
                : pos.map((p) => <SelectItem key={p.id} value={p.id}>{p.po_number} · {p.buyer_name || "—"}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="qr-download" onClick={dl} disabled={!pid} className="gap-2"><Download className="h-4 w-4" /> Download QR labels</Button>
      </div>
      {po && (
        <p className="mt-3 text-xs text-muted-foreground">{(po.items || []).length} line item(s) in this PO. Make sure each SKU has a cost sheet so carton counts are correct.</p>
      )}
    </SectionCard>
  );
}

/* 2. Assembly instruction PDF per SKU */
function Assembly({ skus }) {
  const [sku, setSku] = useState("");
  const sel = skus.find((s) => s.sku === sku);
  return (
    <SectionCard title="Assembly instruction PDF" hint="Pick a SKU and open its assembly-instruction PDF. Upload the PDF per product in Product Costing (Boxes & CBM section).">
      <div className="grid max-w-xl gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
        <div className="space-y-1">
          <Label className="text-xs">SKU</Label>
          <Select value={sku} onValueChange={setSku}>
            <SelectTrigger data-testid="assembly-sku-select"><SelectValue placeholder="Select a SKU" /></SelectTrigger>
            <SelectContent>
              {skus.length === 0 ? <div className="px-2 py-1.5 text-xs text-muted-foreground">No cost sheets</div>
                : skus.map((s) => <SelectItem key={s.sku} value={s.sku}>{s.sku} · {s.product_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {sel?.assembly_pdf
          ? <a href={authFileUrl(sel.assembly_pdf)} target="_blank" rel="noreferrer" data-testid="assembly-open">
              <Button className="gap-2"><Eye className="h-4 w-4" /> Open / Print PDF</Button>
            </a>
          : <Button disabled className="gap-2"><Eye className="h-4 w-4" /> Open / Print PDF</Button>}
      </div>
      {sel && !sel.assembly_pdf && (
        <p className="mt-3 text-xs text-ochre">No assembly PDF uploaded for this SKU. Add it in Product Costing → edit this cost sheet.</p>
      )}
    </SectionCard>
  );
}

/* 3. Export carton labels */
function ExportLabels({ skus, dimsOf }) {
  const empty = { buyer: "", buyer_address: "", sku: "", product_name: "", po_no: "", net_weight: 0, gross_weight: 0, dimensions: "", total_boxes: 1 };
  const [f, setF] = useState(empty);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const onSku = (sku) => {
    const s = skus.find((x) => x.sku === sku);
    setF((p) => ({ ...p, sku, product_name: s?.product_name || "", net_weight: s?.net_weight || 0, gross_weight: s?.gross_weight || 0, dimensions: dimsOf(s) }));
  };
  const dl = async () => {
    try {
      await downloadPost("/printing/shipping-labels.pdf", { ...f, net_weight: Number(f.net_weight) || 0, gross_weight: Number(f.gross_weight) || 0, total_boxes: Number(f.total_boxes) || 1 }, "shipping_labels.pdf");
    } catch (e) { toast.error("Could not generate labels"); }
  };
  return (
    <SectionCard title="Export packaging labels" hint="Carton shipping marks — one label per carton. Pick a SKU to prefill weights & dimensions from its cost sheet, then edit the consignee details.">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <Label className="text-xs">SKU</Label>
          <Select value={f.sku} onValueChange={onSku}>
            <SelectTrigger data-testid="label-sku-select"><SelectValue placeholder="Select a SKU" /></SelectTrigger>
            <SelectContent>
              {skus.map((s) => <SelectItem key={s.sku} value={s.sku}>{s.sku} · {s.product_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1"><Label className="text-xs">Product name</Label>
          <Input data-testid="label-product" value={f.product_name} onChange={(e) => set("product_name", e.target.value)} /></div>
        <div className="space-y-1"><Label className="text-xs">Consignee / Buyer</Label>
          <Input data-testid="label-buyer" value={f.buyer} onChange={(e) => set("buyer", e.target.value)} /></div>
        <div className="space-y-1"><Label className="text-xs">PO No</Label>
          <Input data-testid="label-po" value={f.po_no} onChange={(e) => set("po_no", e.target.value)} /></div>
        <div className="space-y-1 sm:col-span-2"><Label className="text-xs">Buyer address</Label>
          <Input data-testid="label-address" value={f.buyer_address} onChange={(e) => set("buyer_address", e.target.value)} /></div>
        <div className="space-y-1"><Label className="text-xs">Dimensions</Label>
          <Input data-testid="label-dims" value={f.dimensions} onChange={(e) => set("dimensions", e.target.value)} /></div>
        <div className="grid grid-cols-3 gap-2">
          <div className="space-y-1"><Label className="text-xs">Net wt (kg)</Label>
            <Input type="number" data-testid="label-net" value={f.net_weight} onChange={(e) => set("net_weight", e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Gross wt (kg)</Label>
            <Input type="number" data-testid="label-gross" value={f.gross_weight} onChange={(e) => set("gross_weight", e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Total boxes</Label>
            <Input type="number" data-testid="label-total" value={f.total_boxes} onChange={(e) => set("total_boxes", e.target.value)} /></div>
        </div>
      </div>
      <Button data-testid="label-download" onClick={dl} disabled={!f.buyer} className="mt-4 gap-2"><Download className="h-4 w-4" /> Download labels</Button>
    </SectionCard>
  );
}

/* 4. Packing list */
function PackingList({ skus, pos }) {
  const [hdr, setHdr] = useState({ buyer: "", buyer_address: "", po_no: "", invoice_no: "", date: new Date().toISOString().slice(0, 10) });
  const [lines, setLines] = useState([{ sku: "", product_name: "", units: "" }]);
  const [preview, setPreview] = useState(null);
  const setH = (k, v) => setHdr((p) => ({ ...p, [k]: v }));
  const upLine = (i, k, v) => setLines((p) => { const c = [...p]; c[i] = { ...c[i], [k]: v }; return c; });
  const pickSku = (i, sku) => { const s = skus.find((x) => x.sku === sku); upLine(i, "sku", sku); upLine(i, "product_name", s?.product_name || ""); };
  const addLine = () => setLines((p) => [...p, { sku: "", product_name: "", units: "" }]);
  const rmLine = (i) => setLines((p) => p.filter((_, x) => x !== i));
  const loadFromPo = (pid) => {
    const p = pos.find((x) => x.id === pid); if (!p) return;
    setHdr((h) => ({ ...h, buyer: p.buyer_name || "", po_no: p.buyer_po_no || p.po_number || "" }));
    const ls = (p.items || []).filter((it) => it.sku).map((it) => ({ sku: it.sku, product_name: it.name || "", units: String(it.qty || "") }));
    setLines(ls.length ? ls : [{ sku: "", product_name: "", units: "" }]);
    toast.success("Loaded from PO");
  };

  const body = () => ({ ...hdr, lines: lines.filter((l) => l.sku && Number(l.units) > 0).map((l) => ({ sku: l.sku, product_name: l.product_name, units: Number(l.units) || 0 })) });
  const canRun = body().lines.length > 0;

  const doPreview = async () => {
    try { const { data } = await api.post("/printing/packing-list", body()); setPreview(data); }
    catch (e) { toast.error("Could not build packing list"); }
  };
  const doDownload = async () => {
    try { await downloadPost("/printing/packing-list.pdf", body(), "packing_list.pdf"); }
    catch (e) { toast.error("Could not generate PDF"); }
  };

  return (
    <SectionCard title="Export packing list" hint="Add SKUs & units, or load a Purchase Order to auto-fill. Carton counts (per unit) and weights come from each SKU's cost sheet; box numbers run automatically.">
      {pos.length > 0 && (
        <div className="mb-4 max-w-sm space-y-1">
          <Label className="flex items-center gap-1 text-xs"><Building2 className="h-3.5 w-3.5" /> Load from Purchase Order</Label>
          <Select onValueChange={loadFromPo}>
            <SelectTrigger data-testid="pl-load-po"><SelectValue placeholder="Pick a PO to auto-fill" /></SelectTrigger>
            <SelectContent>{pos.map((p) => <SelectItem key={p.id} value={p.id}>{p.po_number} · {p.buyer_name || "—"}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-1"><Label className="text-xs">Buyer / Consignee</Label>
          <Input data-testid="pl-buyer" value={hdr.buyer} onChange={(e) => setH("buyer", e.target.value)} /></div>
        <div className="space-y-1"><Label className="text-xs">PO No</Label>
          <Input data-testid="pl-po" value={hdr.po_no} onChange={(e) => setH("po_no", e.target.value)} /></div>
        <div className="space-y-1"><Label className="text-xs">Invoice No</Label>
          <Input data-testid="pl-invoice" value={hdr.invoice_no} onChange={(e) => setH("invoice_no", e.target.value)} /></div>
        <div className="space-y-1 sm:col-span-2"><Label className="text-xs">Buyer address</Label>
          <Input data-testid="pl-address" value={hdr.buyer_address} onChange={(e) => setH("buyer_address", e.target.value)} /></div>
        <div className="space-y-1"><Label className="text-xs">Date</Label>
          <Input type="date" data-testid="pl-date" value={hdr.date} onChange={(e) => setH("date", e.target.value)} /></div>
      </div>

      <div className="mt-5 space-y-2">
        <div className="flex items-center justify-between">
          <p className="font-heading text-sm font-bold">Items</p>
          <Button size="sm" variant="outline" data-testid="pl-add-line" onClick={addLine} className="h-7 gap-1 text-xs"><Plus className="h-3 w-3" /> Add SKU</Button>
        </div>
        {lines.map((l, i) => (
          <div key={i} className="grid grid-cols-12 items-end gap-2">
            <div className="col-span-7 space-y-1 sm:col-span-8">
              <Label className="text-[10px] uppercase text-muted-foreground">SKU</Label>
              <Select value={l.sku} onValueChange={(v) => pickSku(i, v)}>
                <SelectTrigger data-testid={`pl-sku-${i}`}><SelectValue placeholder="Select SKU" /></SelectTrigger>
                <SelectContent>{skus.map((s) => <SelectItem key={s.sku} value={s.sku}>{s.sku} · {s.product_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-4 space-y-1 sm:col-span-3">
              <Label className="text-[10px] uppercase text-muted-foreground">Units</Label>
              <Input type="number" data-testid={`pl-units-${i}`} value={l.units} onChange={(e) => upLine(i, "units", e.target.value)} />
            </div>
            <Button type="button" variant="ghost" size="icon" className="col-span-1 h-9 w-9 text-muted-foreground hover:text-destructive" onClick={() => rmLine(i)}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button variant="outline" data-testid="pl-preview" onClick={doPreview} disabled={!canRun} className="gap-2"><Eye className="h-4 w-4" /> Preview</Button>
        <Button data-testid="pl-download" onClick={doDownload} disabled={!canRun} className="gap-2"><Download className="h-4 w-4" /> Download PDF</Button>
      </div>

      {preview && (
        <div className="mt-5 overflow-x-auto" data-testid="pl-preview-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Box No</TableHead><TableHead>SKU</TableHead><TableHead>Description</TableHead>
                <TableHead className="text-right">Pkgs</TableHead><TableHead>Dimensions</TableHead>
                <TableHead className="text-right">N.Wt</TableHead><TableHead className="text-right">G.Wt</TableHead><TableHead className="text-right">CBM</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {preview.rows.map((r, i) => (
                <TableRow key={i} className="hover:bg-muted/40">
                  <TableCell className="font-semibold">{r.box_label}</TableCell>
                  <TableCell>{r.sku || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{r.product_name || "—"}</TableCell>
                  <TableCell className="text-right">{r.boxes}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.dimensions}</TableCell>
                  <TableCell className="text-right">{money(r.net_weight)}</TableCell>
                  <TableCell className="text-right">{money(r.gross_weight)}</TableCell>
                  <TableCell className="text-right">{r.cbm}</TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-primary/5 font-bold">
                <TableCell>Total</TableCell><TableCell colSpan={2}></TableCell>
                <TableCell className="text-right" data-testid="pl-total-boxes">{preview.totals.boxes}</TableCell>
                <TableCell></TableCell>
                <TableCell className="text-right">{money(preview.totals.net_weight)}</TableCell>
                <TableCell className="text-right">{money(preview.totals.gross_weight)}</TableCell>
                <TableCell className="text-right">{preview.totals.cbm}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}
    </SectionCard>
  );
}


/* 5. Commercial invoice */
function Invoice({ skus, pos }) {
  const [hdr, setHdr] = useState({ buyer: "", buyer_address: "", buyer_gst: "", invoice_no: "", po_no: "", date: new Date().toISOString().slice(0, 10), currency: "USD" });
  const [lines, setLines] = useState([{ sku: "", product_name: "", units: "", rate: "" }]);
  const [preview, setPreview] = useState(null);
  const setH = (k, v) => setHdr((p) => ({ ...p, [k]: v }));
  const upLine = (i, k, v) => setLines((p) => { const c = [...p]; c[i] = { ...c[i], [k]: v }; return c; });
  const rateFor = (sku, cur) => { const s = skus.find((x) => x.sku === sku); const r = cur === "USD" ? s?.final_price_usd : s?.final_price_inr; return r ? String(Math.round(r * 100) / 100) : ""; };
  const pickSku = (i, sku) => { const s = skus.find((x) => x.sku === sku); upLine(i, "sku", sku); upLine(i, "product_name", s?.product_name || ""); upLine(i, "rate", rateFor(sku, hdr.currency)); };
  const setCurrency = (cur) => { setHdr((h) => ({ ...h, currency: cur })); setLines((ls) => ls.map((l) => ({ ...l, rate: l.sku ? rateFor(l.sku, cur) : l.rate }))); };
  const addLine = () => setLines((p) => [...p, { sku: "", product_name: "", units: "", rate: "" }]);
  const rmLine = (i) => setLines((p) => p.filter((_, x) => x !== i));
  const loadFromPo = (pid) => {
    const p = pos.find((x) => x.id === pid); if (!p) return;
    setHdr((h) => ({ ...h, buyer: p.buyer_name || "", po_no: p.buyer_po_no || p.po_number || "" }));
    const ls = (p.items || []).filter((it) => it.sku).map((it) => ({ sku: it.sku, product_name: it.name || "", units: String(it.qty || ""), rate: rateFor(it.sku, hdr.currency) }));
    setLines(ls.length ? ls : [{ sku: "", product_name: "", units: "", rate: "" }]);
    toast.success("Loaded from PO");
  };

  const body = () => ({ ...hdr, lines: lines.filter((l) => l.sku && Number(l.units) > 0).map((l) => ({ sku: l.sku, product_name: l.product_name, units: Number(l.units) || 0, rate: Number(l.rate) || 0 })) });
  const canRun = body().lines.length > 0;
  const doPreview = async () => { try { const { data } = await api.post("/printing/invoice", body()); setPreview(data); } catch (e) { toast.error("Could not build invoice"); } };
  const doDownload = async () => { try { await downloadPost("/printing/invoice.pdf", body(), "commercial_invoice.pdf"); } catch (e) { toast.error("Could not generate PDF"); } };
  const doBundle = async () => { try { await downloadPost("/printing/bundle.pdf", body(), "export_packet.pdf"); toast.success("Export packet downloaded"); } catch (e) { toast.error("Could not generate packet"); } };

  return (
    <SectionCard title="Commercial invoice" hint="Add SKUs & quantities, or load a Purchase Order. Unit prices auto-fill from each SKU's cost sheet (editable); switch currency any time.">
      {pos.length > 0 && (
        <div className="mb-4 max-w-sm space-y-1">
          <Label className="flex items-center gap-1 text-xs"><Building2 className="h-3.5 w-3.5" /> Load from Purchase Order</Label>
          <Select onValueChange={loadFromPo}>
            <SelectTrigger data-testid="inv-load-po"><SelectValue placeholder="Pick a PO to auto-fill" /></SelectTrigger>
            <SelectContent>{pos.map((p) => <SelectItem key={p.id} value={p.id}>{p.po_number} · {p.buyer_name || "—"}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-1"><Label className="text-xs">Buyer / Consignee</Label>
          <Input data-testid="inv-buyer" value={hdr.buyer} onChange={(e) => setH("buyer", e.target.value)} /></div>
        <div className="space-y-1"><Label className="text-xs">Invoice No</Label>
          <Input data-testid="inv-no" value={hdr.invoice_no} onChange={(e) => setH("invoice_no", e.target.value)} /></div>
        <div className="space-y-1"><Label className="text-xs">Buyer PO No</Label>
          <Input data-testid="inv-po" value={hdr.po_no} onChange={(e) => setH("po_no", e.target.value)} /></div>
        <div className="space-y-1 sm:col-span-2"><Label className="text-xs">Buyer address</Label>
          <Input data-testid="inv-address" value={hdr.buyer_address} onChange={(e) => setH("buyer_address", e.target.value)} /></div>
        <div className="space-y-1"><Label className="text-xs">Buyer GST / Tax No</Label>
          <Input data-testid="inv-gst" value={hdr.buyer_gst} onChange={(e) => setH("buyer_gst", e.target.value)} /></div>
        <div className="space-y-1"><Label className="text-xs">Date</Label>
          <Input type="date" data-testid="inv-date" value={hdr.date} onChange={(e) => setH("date", e.target.value)} /></div>
        <div className="space-y-1"><Label className="text-xs">Currency</Label>
          <Select value={hdr.currency} onValueChange={setCurrency}>
            <SelectTrigger data-testid="inv-currency"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="USD">USD</SelectItem><SelectItem value="INR">INR</SelectItem></SelectContent>
          </Select></div>
      </div>

      <div className="mt-5 space-y-2">
        <div className="flex items-center justify-between">
          <p className="font-heading text-sm font-bold">Items</p>
          <Button size="sm" variant="outline" data-testid="inv-add-line" onClick={addLine} className="h-7 gap-1 text-xs"><Plus className="h-3 w-3" /> Add SKU</Button>
        </div>
        {lines.map((l, i) => (
          <div key={i} className="grid grid-cols-12 items-end gap-2">
            <div className="col-span-6 space-y-1">
              <Label className="text-[10px] uppercase text-muted-foreground">SKU</Label>
              <Select value={l.sku} onValueChange={(v) => pickSku(i, v)}>
                <SelectTrigger data-testid={`inv-sku-${i}`}><SelectValue placeholder="Select SKU" /></SelectTrigger>
                <SelectContent>{skus.map((s) => <SelectItem key={s.sku} value={s.sku}>{s.sku} · {s.product_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1">
              <Label className="text-[10px] uppercase text-muted-foreground">Qty</Label>
              <Input type="number" data-testid={`inv-units-${i}`} value={l.units} onChange={(e) => upLine(i, "units", e.target.value)} />
            </div>
            <div className="col-span-3 space-y-1">
              <Label className="text-[10px] uppercase text-muted-foreground">Unit price ({hdr.currency})</Label>
              <Input type="number" data-testid={`inv-rate-${i}`} value={l.rate} onChange={(e) => upLine(i, "rate", e.target.value)} />
            </div>
            <Button type="button" variant="ghost" size="icon" className="col-span-1 h-9 w-9 text-muted-foreground hover:text-destructive" onClick={() => rmLine(i)}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button variant="outline" data-testid="inv-preview" onClick={doPreview} disabled={!canRun} className="gap-2"><Eye className="h-4 w-4" /> Preview</Button>
        <Button data-testid="inv-download" onClick={doDownload} disabled={!canRun} className="gap-2"><Download className="h-4 w-4" /> Download Invoice</Button>
        <Button variant="secondary" data-testid="inv-bundle" onClick={doBundle} disabled={!canRun} className="gap-2"><PackageOpen className="h-4 w-4" /> Download Full Packet</Button>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">Full Packet = Commercial Invoice + Packing List + Carton Labels in one PDF.</p>

      {preview && (
        <div className="mt-5 overflow-x-auto" data-testid="inv-preview-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>SKU</TableHead><TableHead>Description</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Unit Price ({preview.currency})</TableHead>
                <TableHead className="text-right">Amount ({preview.currency})</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {preview.rows.map((r, i) => (
                <TableRow key={i} className="hover:bg-muted/40">
                  <TableCell>{r.sku || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{r.product_name || "—"}</TableCell>
                  <TableCell className="text-right">{r.units}</TableCell>
                  <TableCell className="text-right">{money(r.rate)}</TableCell>
                  <TableCell className="text-right">{money(r.amount)}</TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-primary/5 font-bold">
                <TableCell colSpan={2}>Total</TableCell>
                <TableCell className="text-right">{preview.totals.units}</TableCell>
                <TableCell></TableCell>
                <TableCell className="text-right" data-testid="inv-total-amount">{money(preview.totals.amount)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}
    </SectionCard>
  );
}
